import { requestWithAuth } from "./request";

// Report reasons accepted by /feed/report (validated server-side against reportReasonMap).
export type ReportReason = "fake" | "abusive" | "unrelated" | "other";

// Saved feed preferences (28-05). categoryFilters are STRING categoryId values matching
// Store.categories[].categoryId (the Category _id string) — NOT slugs, NOT ObjectIds sent raw.
export type FeedPreferences = {
  categoryFilters: string[];
  mutedStoreIds: string[];
  hiddenReviewIds: string[];
  watchedOnly: boolean;
};

// Client for the auth-gated /api/feed endpoints (28-03). Base URL is `/api`, so paths
// here do NOT include the `/api` prefix. requestWithAuth unwraps the { ok, data } envelope.
export const feedService = {
  // ── Watch (store follow) ──
  watchStore: async (storeId: string): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: "/feed/follows",
      method: "POST",
      data: { storeId },
    });
  },

  unwatchStore: async (storeId: string): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: `/feed/follows/${storeId}`,
      method: "DELETE",
    });
  },

  listWatched: async (): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: "/feed/follows",
      method: "GET",
    });
  },

  // ── Not interested: hide a single review ──
  hideReview: async (reviewId: string): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: "/feed/hide",
      method: "POST",
      data: { reviewId },
    });
  },

  unhideReview: async (reviewId: string): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: `/feed/hide/${reviewId}`,
      method: "DELETE",
    });
  },

  // ── Not interested: mute a whole store ──
  muteStore: async (storeId: string): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: "/feed/mute",
      method: "POST",
      data: { storeId },
    });
  },

  unmuteStore: async (storeId: string): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: `/feed/mute/${storeId}`,
      method: "DELETE",
    });
  },

  // ── Report (reuses the existing report service server-side; never hides the review) ──
  reportReview: async ({
    reviewId,
    reason,
    note,
  }: {
    reviewId: string;
    reason: ReportReason;
    note?: string;
  }): Promise<unknown> => {
    return requestWithAuth<unknown>({
      path: "/feed/report",
      method: "POST",
      data: { reviewId, reason, ...(note ? { note } : {}) },
    });
  },

  // ── Saved feed preferences (28-05) ──
  // GET reads the caller's prefs; PUT writes ONLY categoryFilters + watchedOnly (the backend
  // rejects any attempt to write hiddenReviewIds/mutedStoreIds via this path).
  getPreferences: async (): Promise<FeedPreferences> => {
    return requestWithAuth<FeedPreferences>({
      path: "/feed/preferences",
      method: "GET",
    });
  },

  updatePreferences: async (patch: {
    categoryFilters?: string[];
    watchedOnly?: boolean;
  }): Promise<FeedPreferences> => {
    return requestWithAuth<FeedPreferences>({
      path: "/feed/preferences",
      method: "PUT",
      data: patch,
    });
  },
};
